import * as ProductUtils from "product-utils";
import { shopifyFormatCurrency, fetchAndRenderProductRecommendations, initializeVideoLightbox } from "product-utils";

/**
 * Initialize the product functionality
 * This is the main entry point for the product page
 */
(function () {
  try {
    // Get the product node and data
    const node = document.querySelector("[data-template-product]");
    if (!node) {
      console.info("Product template not found in the DOM");
      return;
    }

    const dataElement = node.querySelector("[data-data]");
    if (!dataElement) {
      console.info("Product data element not found in the DOM");
      return;
    }

    // Parse the product data
    let productData;
    try {
      productData = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error("Error parsing product data:", error);
      return;
    }

    if (!productData || !productData.variants) {
      console.info("Invalid product data structure");
      return;
    }

    const sectionId = node.dataset.sectionId;

    // Initialize product functionality
    initializeProduct(node, productData, sectionId);
  } catch (error) {
    console.error("Error initializing product:", error);
  }
})();

/**
 * Initialize all product functionality
 * @param {HTMLElement} node - The product container node
 * @param {Object} productData - The product data
 * @param {string} sectionId - The section ID
 */
function initializeProduct(node, productData, sectionId) {
  // Get initial selected options
  const selectedOptions = ProductUtils.getOptions(node, productData);
  const matchingVariant = ProductUtils.getVariant(node, productData, selectedOptions);

  // Call update availability on initial load
  ProductUtils.updateOptionAvailability(node, productData, selectedOptions);

  // Update add to cart button on initial load
  ProductUtils.updateAddToCartButton(node, matchingVariant, productData);

  // Setup change handlers for all option inputs
  ProductUtils.setupOptionChangeHandlers(node, productData);

  // Setup hover handlers for option name display
  ProductUtils.setupOptionNameHoverHandlers(node, productData);

  // Setup form submission handler for cart drawer functionality
  setupProductFormSubmission();

  // Preload common variants for faster navigation
  preloadVariants(productData, matchingVariant, sectionId);

  // Initialize UI components
  setupScrollToAddToCart();
  initializeDynamicStickyHeaderAnchors();
  setupReadMoreLess();
  setupComparisonTable();
  initializeProductRecommendations(productData);

  // Initialize sticky information position adjustment
  initializeStickyInformation();

  // Initialize delayed scroll functionality
  initializeDelayedScroll();

  initializeVideoLightbox();

  // Bind SKU View more behavior for current render
  ProductUtils.initializeSkuViewMore(node);
}

/**
 * Setup form submission handler to open cart drawer instead of redirecting
 */
function setupProductFormSubmission() {
  const productForm = document.getElementById("product-form");
  if (!productForm) return;

  // Check if accessories component exists and might handle form submission
  const hasAccessoriesComponent = document.querySelector("recommended-accessories");
  if (hasAccessoriesComponent) {
    // Accessories component will handle form submission
    return;
  }

  productForm.addEventListener("submit", handleProductFormSubmit);
}

/**
 * Handle product form submission
 * @param {Event} event - Submit event
 */
function handleProductFormSubmit(event) {
  try {
    event.preventDefault();

    const addToCartButton = document.querySelector("[data-add-to-cart]");

    // Show loading state
    if (addToCartButton) {
      addToCartButton.classList.add("loading");
      addToCartButton.setAttribute("disabled", "disabled");
    }

    // Get the main product variant ID from the select element
    let mainProductVariantId = null;
    const variantSelector = event.target.querySelector('select[name="id"]');

    if (variantSelector) {
      mainProductVariantId = formatVariantId(variantSelector.value);
    }

    if (!mainProductVariantId) {
      handleFormErrors({ description: "No product variant selected." });
      resetButtonState(addToCartButton);
      return;
    }

    // Prepare item for cart
    const items = [
      {
        id: mainProductVariantId,
        quantity: 1,
      },
    ];

    addItemToCart(items, addToCartButton);
  } catch (error) {
    console.error("Error handling form submission:", error);
    handleFormErrors({ description: "An unexpected error occurred. Please try again." });

    const addToCartButton = document.querySelector("[data-add-to-cart]");
    resetButtonState(addToCartButton);
  }
}

/**
 * Get cart sections for rendering in the response
 * @returns {Array} - Array of section IDs
 */
function getCartSections() {
  // Use the theme's cart:grouped-sections event pattern
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
  );

  return sectionsToBundle;
}

/**
 * Add items to cart using the Shopify AJAX API
 * @param {Array} items - Array of items to add
 * @param {HTMLElement} addToCartButton - The add to cart button element
 */
function addItemToCart(items, addToCartButton) {
  if (!items || !items.length) {
    handleFormErrors({ description: "No items to add to cart." });
    resetButtonState(addToCartButton);
    return;
  }

  // Verify all items have valid IDs before sending
  const validItems = items.filter((item) => {
    const isValid = item.id && String(item.id).length > 0;
    return isValid;
  });

  if (validItems.length === 0) {
    handleFormErrors({ description: "No valid items to add to cart." });
    resetButtonState(addToCartButton);
    return;
  }

  // Get sections to bundle using the theme's event pattern
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
  );

  // Use the theme's fetch configuration
  const config = FoxTheme.utils.fetchConfig("javascript");
  config.headers["X-Requested-With"] = "XMLHttpRequest";

  // Create the request body
  config.body = JSON.stringify({
    items: validItems,
    sections: sectionsToBundle,
    sections_url: window.location.pathname,
  });

  try {
    // Use FoxTheme.routes for the URL
    const url = FoxTheme.routes.cart_add_url;

    // Make the fetch request following the theme's pattern
    fetch(url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          handleFormErrors(parsedState);
          return;
        }

        // Follow the theme's pattern: fetch cart JSON and merge sections
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
        cartJson["sections"] = parsedState["sections"];

        // Update cart state using the theme's pubsub system
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

        // Dispatch the theme's product added event
        document.dispatchEvent(
          new CustomEvent("product-ajax:added", {
            detail: {
              product: parsedState,
            },
          })
        );

        // Show cart drawer if available (following theme pattern)
        showCartDrawer(addToCartButton);
      })
      .catch((error) => {
        console.error("Error adding items to cart:", error);
        handleFormErrors({
          description: "An error occurred adding items to your cart. Please try again.",
        });
      })
      .finally(() => {
        resetButtonState(addToCartButton);
      });
  } catch (error) {
    console.error("Error in cart add request:", error);
    handleFormErrors({ description: "An unexpected error occurred. Please try again." });
    resetButtonState(addToCartButton);
  }
}

/**
 * Show cart drawer following the theme's pattern
 * @param {HTMLElement} lastSubmittedElement - The element that triggered the submission
 */
function showCartDrawer(lastSubmittedElement) {
  // Find the cart drawer element
  const cartDrawerElement = document.querySelector("cart-drawer");

  if (!cartDrawerElement) return;

  // Check if we're in a quick view modal (following theme pattern)
  const quickViewModal = lastSubmittedElement?.closest("quick-view-modal");

  if (quickViewModal) {
    if (cartDrawerElement && !cartDrawerElement.open) {
      document.body.addEventListener(
        quickViewModal.events.handleAfterHide,
        () => {
          setTimeout(() => {
            cartDrawerElement.show(lastSubmittedElement);
          });
        },
        { once: true }
      );
    }
    quickViewModal.hide(true);
  } else {
    cartDrawerElement.show(lastSubmittedElement);
  }
}

/**
 * Reset the add to cart button state
 * @param {HTMLElement} addToCartButton - The add to cart button element
 */
function resetButtonState(addToCartButton) {
  if (addToCartButton) {
    addToCartButton.classList.remove("loading");
    addToCartButton.removeAttribute("disabled");
  }
}

/**
 * Handle errors from cart addition
 * @param {Object} error - Error data
 */
function handleFormErrors(error) {
  // Display error message
  const productForm = document.getElementById("product-form");
  if (!productForm) return;

  const errorContainer = productForm.querySelector(".product-form__error-message-wrapper");
  const errorMessage = productForm.querySelector(".product-form__error-message");

  if (errorContainer && errorMessage) {
    errorMessage.textContent = error.description;
    errorContainer.removeAttribute("hidden");

    // Hide after a delay
    setTimeout(() => {
      errorContainer.setAttribute("hidden", "");
    }, 5000);
  }

  // Dispatch error event using the theme's event name
  document.dispatchEvent(
    new CustomEvent("product-ajax:error", {
      detail: {
        errorMessage: error.description,
      },
    })
  );
}

/**
 * Ensure variant ID is properly formatted
 * @param {string|number} id - The variant ID to format
 * @returns {string} - The formatted ID
 */
function formatVariantId(id) {
  // Convert to string and remove any non-numeric characters
  return String(id).replace(/[^0-9]/g, "");
}

/**
 * Preload common variants to improve performance
 * @param {Object} productData - The product data
 * @param {Object} matchingVariant - The current selected variant
 * @param {string} sectionId - The section ID
 */
function preloadVariants(productData, matchingVariant, sectionId) {
  if (!productData || !productData.variants || !productData.variants.length) return;

  if (productData.variants.length > 1 && matchingVariant) {
    const variantsToPreload = productData.variants
      .filter((v) => v.id !== matchingVariant.id)
      .slice(0, 3)
      .map((v) => v.id);

    ProductUtils.preloadVariantData(productData.handle, variantsToPreload, sectionId);
  }
}

/**
 * Setup scroll to add-to-cart button functionality
 */
function setupScrollToAddToCart() {
  const scrollButtons = document.querySelectorAll("[data-scroll-to-top]");
  if (scrollButtons.length === 0) return;

  scrollButtons.forEach((button) => {
    button.addEventListener("click", (event) => {
      event.preventDefault();

      // Find the add-to-cart button
      const addToCartButton = document.querySelector("[data-add-to-cart]");
      if (!addToCartButton) return;

      // Calculate position to center the button in the viewport
      const buttonRect = addToCartButton.getBoundingClientRect();
      const buttonTop = buttonRect.top + window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate where to scroll (center of viewport)
      const scrollToY = buttonTop - windowHeight / 2 + buttonRect.height / 2;

      // Smooth scroll to position
      window.scrollTo({
        top: scrollToY,
        behavior: "smooth",
      });
    });
  });
}

/**
 * Update the prices in sticky headers
 * @param {Object} matchingVariant - The current selected variant
 */
function updateStickyHeaderPrices(matchingVariant) {
  if (!matchingVariant) return;

  const priceElements = document.querySelectorAll('[data-render-block="price"]');
  priceElements.forEach((priceElement) => {
    // Check if element is within a sticky header
    const isInStickyHeader = priceElement.closest(".grafikr-product__sticky-header");
    if (!isInStickyHeader) return;

    // Update the price display
    const hasCompareAtPrice = matchingVariant.compare_at_price > matchingVariant.price;
    const formatString = window.theme?.shopSettings?.moneyFormat || "{{ amount }}";

    try {
      if (hasCompareAtPrice) {
        priceElement.innerHTML = `
          <span class="grafikr-product__sticky-header__now">${window.theme?.strings?.now || "Now"}</span>
          <span class="grafikr-product__sticky-header__price">${shopifyFormatCurrency(
            matchingVariant.price,
            formatString
          )}</span>
          <del class="grafikr-product__sticky-header__compare-at-price">${shopifyFormatCurrency(
            matchingVariant.compare_at_price,
            formatString
          )}</del>
        `;
      } else {
        priceElement.innerHTML = `
          <span class="grafikr-product__sticky-header__price">${shopifyFormatCurrency(
            matchingVariant.price,
            formatString
          )}</span>
        `;
      }
    } catch (error) {
      console.error("Error updating sticky header price:", error);
    }
  });
}

/**
 * Setup read more/less functionality for product descriptions
 */
function setupReadMoreLess() {
  const readMoreButtons = document.querySelectorAll(
    ".grafikr-product__discovery__about__read-more:not([data-event-attached])"
  );
  if (readMoreButtons.length === 0) return;

  readMoreButtons.forEach((button) => {
    // Mark this button as having an event attached
    button.setAttribute("data-event-attached", "true");

    button.addEventListener("click", () => {
      try {
        // Find the line-clamped element that's a sibling of this button's parent
        const container = button.closest(".grafikr-accordion__content");
        if (!container) return;

        const lineClampedElement = container.querySelector(
          ".grafikr-product__discovery__about__description__line-clamp"
        );
        if (!lineClampedElement) return;

        lineClampedElement.classList.toggle("expanded");
        // Also toggle a class on the button itself
        button.classList.toggle("expanded");
      } catch (error) {
        console.error("Error in read more/less toggle:", error);
      }
    });
  });
}

/**
 * Setup comparison table
 */
function setupComparisonTable() {
  try {
    const targetElements = document.querySelectorAll("[data-comparison-table-template]");
    const templateElement = document.getElementById("comparison-table-template");

    if (!templateElement || targetElements.length === 0) return;

    targetElements.forEach((target) => {
      // Check if the target already has a comparison table
      if (target.querySelector(".comparison-table__container")) {
        return;
      }

      // Clone the template content
      const content = templateElement.content.cloneNode(true);
      // Insert into the target element
      target.appendChild(content);
    });
  } catch (error) {
    console.error("Error setting up comparison table:", error);
  }
}

/**
 * Initialize product recommendations
 * @param {Object} productData - The product data
 */
function initializeProductRecommendations(productData) {
  try {
    if (!productData || !productData.id) return;

    // Check if we have product recommendation containers
    const recommendationContainers = document.querySelectorAll(
      `[data-product-recommendations-container="${productData.id}"]`
    );

    if (recommendationContainers.length === 0) return;

    // Fetch and render product recommendations with a limit of 10 products
    fetchAndRenderProductRecommendations(productData.id, 10, "grafikr-product-recommendations");
  } catch (error) {
    console.error("Error initializing product recommendations:", error);
  }
}

/**
 * Initialize the sticky information element to adjust its position while scrolling
 */
function initializeStickyInformation() {
  const stickyInfoElement = document.querySelector("[data-sticky-information]");
  if (!stickyInfoElement) return;

  // Store initial top value from CSS
  const computedStyle = window.getComputedStyle(stickyInfoElement);
  const initialTopValue = 160;

  // Get sticky header height for offset calculations
  const stickyHeaderHeight =
    parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sticky-header-height")) || 0;

  // Set initial attributes
  stickyInfoElement.setAttribute("data-initial-top", initialTopValue);

  // Fixed scroll distance in pixels - how much user needs to scroll to see the full element
  const scrollDistance = 200;

  // Track last scroll position to determine direction
  let lastScrollY = window.scrollY;

  // Set initial position
  stickyInfoElement.style.top = `${initialTopValue}px`;

  // Function to adjust the sticky element's position
  const adjustStickyPosition = () => {
    const windowHeight = window.innerHeight;
    const elementHeight = stickyInfoElement.offsetHeight;
    const currentScrollY = window.scrollY;

    // Determine scroll direction
    const scrollDirection = currentScrollY > lastScrollY ? "down" : "up";

    // Only apply custom sticky behavior if element is taller than viewport
    if (elementHeight > windowHeight) {
      // Calculate how far we can scroll the element up (will be negative)
      const maxTopOffset = windowHeight - elementHeight - stickyHeaderHeight;

      // Get the current position of the element, defaulting to initialTopValue if not set
      let currentTop = initialTopValue;
      if (stickyInfoElement.style.top) {
        currentTop = parseInt(stickyInfoElement.style.top);
        if (isNaN(currentTop)) {
          currentTop = initialTopValue;
        }
      }

      // Amount scrolled since last check
      const scrollAmount = Math.abs(currentScrollY - lastScrollY);

      // Calculate ratio of movement (how much the element should move for each pixel scrolled)
      const moveRatio = Math.abs(initialTopValue - maxTopOffset) / scrollDistance;

      // Calculate how much to move the element
      const moveAmount = scrollAmount * moveRatio;

      let newTopValue;

      if (scrollDirection === "down") {
        // When scrolling down, move the element up (decrease top value)
        newTopValue = currentTop - moveAmount;
        // Don't go lower than maxTopOffset
        newTopValue = Math.max(maxTopOffset, newTopValue);
      } else {
        // When scrolling up, move the element down (increase top value)
        newTopValue = currentTop + moveAmount;
        // Don't go higher than initialTopValue
        newTopValue = Math.min(initialTopValue, newTopValue);
      }

      // Apply the new top value
      stickyInfoElement.style.top = `${newTopValue}px`;

      // Debug output
      // console.log(`Direction: ${scrollDirection}, Current: ${currentTop}, New: ${newTopValue}, Initial: ${initialTopValue}, Max: ${maxTopOffset}`);
    }

    // Update last scroll position
    lastScrollY = currentScrollY;
  };

  // Add scroll event listener
  window.addEventListener("scroll", adjustStickyPosition, { passive: true });

  // Add resize event listener to recalculate on viewport changes
  window.addEventListener(
    "resize",
    () => {
      // Reset scroll position tracking on resize
      lastScrollY = window.scrollY;
      // Reset position to initial
      stickyInfoElement.style.top = `${initialTopValue}px`;
      adjustStickyPosition();
    },
    { passive: true }
  );

  // Initial calculation
  adjustStickyPosition();
}

/**
 * Initialize dynamic sticky header anchors
 * Scans for elements with anchor IDs and show_in_sticky_header settings,
 * then dynamically generates anchor links in the sticky header
 */
function initializeDynamicStickyHeaderAnchors() {
  const stickyHeaderAnchorsContainers = document.querySelectorAll("[data-sticky-header-anchors]");

  if (stickyHeaderAnchorsContainers.length === 0) return;

  // Find all elements that have anchor IDs and should appear in sticky header
  const anchorElements = findAnchorElements();

  // Generate anchor links for each sticky header container
  stickyHeaderAnchorsContainers.forEach((container, index) => {
    // Clear existing anchors
    container.innerHTML = "";

    // Generate anchor links for each element
    anchorElements.forEach((element) => {
      const anchorId = element.id;
      const anchorTitle = element.title;

      if (anchorId && anchorTitle) {
        const listItem = document.createElement("li");
        const anchorLink = document.createElement("a");

        anchorLink.href = `#${anchorId}`;
        anchorLink.textContent = anchorTitle;
        anchorLink.setAttribute("data-anchor-id", anchorId);

        listItem.appendChild(anchorLink);
        container.appendChild(listItem);
      }
    });

    // Set up smooth scrolling for anchor links in this container
    setupAnchorScrolling(container);
  });
}

/**
 * Re-initialize dynamic sticky header anchors
 * This function is exposed to allow re-initialization of anchors
 * after the sticky header content has been updated (e.g., after a variant change)
 */
function reinitializeDynamicStickyHeaderAnchors() {
  initializeDynamicStickyHeaderAnchors();
}

/**
 * Find all elements that should have anchors in the sticky header
 * @returns {Array} Array of objects with id, title, and element
 */
function findAnchorElements() {
  const elements = [];

  // Find all elements with anchor spans that have IDs
  const anchorSpans = document.querySelectorAll(".grafikr-product__discovery__anchor__title[id]");

  anchorSpans.forEach((span) => {
    const anchorId = span.id;
    const anchorTitle = span.textContent.trim();

    // Skip if no ID or no title content
    if (!anchorId || !anchorTitle) return;

    // Check if this element should be shown in sticky header
    // Look for the parent block/section and check its settings
    const parentBlock = span.closest("[data-shopify-editor-block]");
    const parentSection = span.closest("[data-shopify-editor-section]");

    let shouldShow = true; // Default to true if we can't determine the setting

    // Check if we can get the block/section settings
    const container = parentBlock || parentSection;
    if (container) {
      // Check if the container has a data attribute indicating it should be shown
      const showInStickyHeader = container.getAttribute("data-show-in-sticky-header");
      if (showInStickyHeader !== null) {
        shouldShow = showInStickyHeader === "true";
      }
    }

    if (shouldShow) {
      elements.push({
        id: anchorId,
        title: anchorTitle,
        element: span,
      });
    }
  });

  return elements;
}

/**
 * Set up smooth scrolling for anchor links
 * @param {HTMLElement} container - The container with anchor links
 */
function setupAnchorScrolling(container) {
  const anchorLinks = container.querySelectorAll('a[href^="#"]');

  anchorLinks.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();

      const targetId = link.getAttribute("href").substring(1);
      const targetElement = document.getElementById(targetId);

      if (targetElement) {
        // Calculate offset to account for sticky header height
        const stickyHeaderHeight = 120; // Adjust this value as needed
        const elementPosition = targetElement.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - stickyHeaderHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });

        // Update active state
        updateActiveAnchor(link);
      }
    });
  });
}

/**
 * Update the active state of anchor links
 * @param {HTMLElement} activeLink - The currently active link
 */
function updateActiveAnchor(activeLink) {
  // Remove active class from all anchor links
  const allAnchorLinks = document.querySelectorAll("[data-sticky-header-anchors] a");
  allAnchorLinks.forEach((link) => {
    link.classList.remove("active");
  });

  // Add active class to the clicked link
  if (activeLink) {
    activeLink.classList.add("active");
  }
}

/**
 * Initialize delayed scroll functionality for dynamically generated elements
 * Handles scrolling to elements that might be generated after page load
 */
function initializeDelayedScroll() {
  /**
   * Handles delayed scrolling to elements that are generated after page load
   * @param {string} hash - The hash to scroll to (including #)
   */
  function handleDelayedScroll(hash) {
    const targetId = hash.substring(1); // Remove the # symbol
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      // Element exists, scroll immediately
      scrollToElement(targetElement);
    } else {
      // Element doesn't exist yet, wait for it
      waitForElement(targetId);
    }
  }

  /**
   * Waits for an element to appear in the DOM and scrolls to it
   * @param {string} targetId - The ID of the element to wait for
   * @param {number} maxWait - Maximum time to wait in milliseconds
   */
  function waitForElement(targetId, maxWait = 10000) {
    let observer;
    let timeout;

    const cleanup = () => {
      if (observer) observer.disconnect();
      if (timeout) clearTimeout(timeout);
    };

    // Set up MutationObserver to watch for the element
    observer = new MutationObserver(() => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        cleanup();
        // Add a small delay to ensure the element is fully rendered
        setTimeout(() => {
          scrollToElement(targetElement);
        }, 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Set timeout to stop waiting after maxWait milliseconds
    timeout = setTimeout(() => {
      cleanup();
      console.warn(`Element with ID "${targetId}" not found within ${maxWait}ms`);
    }, maxWait);
  }

  /**
   * Smoothly scrolls to an element
   * @param {HTMLElement} element - The element to scroll to
   */
  function scrollToElement(element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }

  // Check if there's a hash in the URL on page load
  if (window.location.hash) {
    handleDelayedScroll(window.location.hash);
  }

  // Listen for hash changes (when clicking anchor links)
  window.addEventListener("hashchange", () => {
    const hash = window.location.hash;
    if (hash) {
      handleDelayedScroll(hash);
    }
  });

  // Prevent default scroll behavior for our specific anchor links and handle delayed scroll
  document.addEventListener("click", (event) => {
    const link = event.target.closest('a[href*="#trustoo-widget-wrapper"]');
    if (link && link.hash) {
      // Check if this is a same-page link or a redirect link
      const linkUrl = new URL(link.href, window.location.origin);
      const currentUrl = new URL(window.location.href);

      // If the link is to the same page (same pathname), prevent default and handle delayed scroll
      if (linkUrl.pathname === currentUrl.pathname) {
        event.preventDefault();
        handleDelayedScroll(link.hash);
      }
      // If it's a different page, let the browser handle the navigation normally
      // The delayed scroll will be handled on page load via the hash check
    }
  });
}

// Expose the reinitialize function globally for external use
window.reinitializeDynamicStickyHeaderAnchors = reinitializeDynamicStickyHeaderAnchors;
